/** Implements the Punch skill: instant melee attack with dash-attack coyote time and hide-break side effect. */
import { InstantSkillEffect } from '../InstantSkillEffect';
import type { CombatComponent } from '../../components/CombatComponent';
import type { DashComponent } from '../../components/DashComponent';
import { ConfigLoader } from '../../systems/ConfigLoader';
import { DebugLog } from '../../debug/DebugLog';

export interface PunchCallbacks {
  getCombat: () => CombatComponent;
  getDash: () => DashComponent;
  breakHide: () => void;
  isConcealed: () => boolean;
  rollSkillSuccess: (skillId: string) => boolean;
}

/** SkillEffect for the Punch ability: delegates to CombatComponent with dash-attack coyote time detection. */
export class PunchSkillEffect extends InstantSkillEffect {
  readonly skillId = 'punch';
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'WHIFF';

  private callbacks: PunchCallbacks;
  /** Tracks whether this skill initiated the current attack on the shared CombatComponent. */
  private _myAttackActive = false;
  private _cooldownMs = 0;
  private readonly _cooldownDurationMs: number;

  get cooldown(): number { return this._cooldownMs; }
  get cooldownDuration(): number { return this._cooldownDurationMs; }

  constructor(callbacks: PunchCallbacks) {
    super();
    this.callbacks = callbacks;
    this._cooldownDurationMs = ConfigLoader.getAttackConfig('punch')?.timing.cooldownMs ?? 0;
  }

  canActivate(): boolean {
    return this._cooldownMs <= 0;
  }

  getBlockReason(): string | null {
    if (this._cooldownMs > 0) return 'cooldown';
    return null;
  }

  /** Start a punch, breaking Hide and delegating to CombatComponent. */
  activate(): boolean {
    const combat = this.callbacks.getCombat();

    // Already attacking → queue the combo buffer but don't claim activation success.
    // Returning false prevents VFX and stamina spend; the buffer fires when recovery ends.
    if (combat.isAttacking) {
      combat.startAttack('punch');
      DebugLog.log('skills', 'punch: buffered punch');
      return false;
    }

    // Fresh attack — roll fizzle before committing (fumble keeps your cover)
    if (!this.callbacks.rollSkillSuccess('punch')) {
      return false;
    }

    const wasConcealed = this.callbacks.isConcealed();
    this.callbacks.breakHide();
    const isDash = this.callbacks.getDash().inDashAttackWindow;
    combat.startAttack('punch', isDash);
    this._myAttackActive = true;
    if (wasConcealed) combat.stealthAttack = true; // Override after beginAttack's reset
    DebugLog.log('skills', `punch: started${isDash ? ' (dash)' : ''}${wasConcealed ? ' (from stealth)' : ''}`);
    return true;
  }

  /** Punch is active only while CombatComponent is running an attack that this skill initiated. */
  isActive(): boolean {
    return this._myAttackActive && this.callbacks.getCombat().isAttacking;
  }

  update(delta: number): void {
    // When our attack finishes, start the cooldown
    if (this._myAttackActive && !this.callbacks.getCombat().isAttacking) {
      this._myAttackActive = false;
      this._cooldownMs = this._cooldownDurationMs;
    }
    if (this._cooldownMs > 0) {
      this._cooldownMs = Math.max(0, this._cooldownMs - delta);
    }
  }
}
